import React from "react";
import {
  Sparkles,
  TrendingUp,
  BarChart3,
  Twitter,
  Zap,
  PieChart,
  Wrench,
  LayoutDashboard,
  LineChart,
  Search,
  HelpCircle,
  Calculator,
  Microscope,
  Table2,
  FileSpreadsheet,
  Database,
  Activity,
} from "lucide-react";
import { type AssistantMode } from "./ModeSelector";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ChatEmptyStateProps {
  mode: AssistantMode;
  onSuggestionClick: (suggestion: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Quick Mode Suggestions - Fast, simple questions
// ─────────────────────────────────────────────────────────────────────────────

const quickCategories = [
  {
    title: "Quick Lookups",
    icon: <Search className="h-4 w-4" />,
    color: "amber",
    suggestions: [
      "What's the current price of BTC?",
      "Show me ETH 24h performance",
      "What's the total crypto market cap?",
    ],
  },
  {
    title: "Simple Questions",
    icon: <HelpCircle className="h-4 w-4" />,
    color: "amber",
    suggestions: [
      "What is a funding rate?",
      "Explain impermanent loss",
      "What are L2 rollups?",
    ],
  },
  {
    title: "Quick Stats",
    icon: <Calculator className="h-4 w-4" />,
    color: "amber",
    suggestions: [
      "Top 5 gainers today",
      "Bitcoin dominance percentage",
      "Total DeFi TVL",
    ],
  },
  {
    title: "News & Updates",
    icon: <Zap className="h-4 w-4" />,
    color: "amber",
    suggestions: [
      "Latest crypto news",
      "Any major announcements today?",
      "What's trending on CT?",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Deep Research Mode Suggestions - Comprehensive analysis
// ─────────────────────────────────────────────────────────────────────────────

const deepCategories = [
  {
    title: "Market Analysis",
    icon: <TrendingUp className="h-4 w-4" />,
    color: "violet",
    suggestions: [
      "Deep dive into BTC price action and on-chain metrics",
      "Comprehensive ETH vs SOL comparison with data",
      "Analyze altcoin momentum with supporting charts",
    ],
  },
  {
    title: "Sector Research",
    icon: <PieChart className="h-4 w-4" />,
    color: "violet",
    suggestions: [
      "Full research report on the L2 ecosystem",
      "State of DeFi 2024 with TVL analysis",
      "AI tokens sector deep dive with market data",
    ],
  },
  {
    title: "Sentiment Analysis",
    icon: <Twitter className="h-4 w-4" />,
    color: "violet",
    suggestions: [
      "Comprehensive sentiment analysis for $ARB",
      "Social metrics and influencer tracking for ETH",
      "CT narrative analysis with engagement data",
    ],
  },
  {
    title: "Data Deep Dive",
    icon: <Microscope className="h-4 w-4" />,
    color: "violet",
    suggestions: [
      "Full funding rates analysis across exchanges",
      "Risk-adjusted returns research with Sharpe ratios",
      "Open interest deep dive with historical trends",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Workspace Mode Suggestions - Dashboard generation
// ─────────────────────────────────────────────────────────────────────────────

const workspaceCategories = [
  {
    title: "Dashboard Templates",
    icon: <LayoutDashboard className="h-4 w-4" />,
    color: "emerald",
    suggestions: [
      "Create a portfolio tracking dashboard",
      "Build a market overview dashboard",
      "Set up a DeFi monitoring workspace",
    ],
  },
  {
    title: "Price Charts",
    icon: <LineChart className="h-4 w-4" />,
    color: "emerald",
    suggestions: [
      "Add BTC and ETH price charts",
      "Create a multi-token comparison chart",
      "Show top 10 tokens by market cap",
    ],
  },
  {
    title: "Data Widgets",
    icon: <BarChart3 className="h-4 w-4" />,
    color: "emerald",
    suggestions: [
      "Add a funding rates widget",
      "Create an open interest tracker",
      "Show sector performance breakdown",
    ],
  },
  {
    title: "Custom Layouts",
    icon: <Wrench className="h-4 w-4" />,
    color: "emerald",
    suggestions: [
      "Build a trading-focused workspace",
      "Create a research dashboard layout",
      "Set up a news and social feed view",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Sheets Mode Suggestions - Two-way copilot interactions
// ─────────────────────────────────────────────────────────────────────────────

const sheetsCategories = [
  {
    title: "Create New Sheets",
    icon: <FileSpreadsheet className="h-4 w-4" />,
    color: "cyan",
    suggestions: [
      "Create a DeFi protocols comparison with TVL",
      "Build a top L1 blockchain analysis sheet",
      "Make an AI tokens watchlist with metrics",
    ],
  },
  {
    title: "Analyze & Insights",
    icon: <Activity className="h-4 w-4" />,
    color: "cyan",
    suggestions: [
      "Analyze this sheet and identify trends",
      "What does the data in this sheet tell me?",
      "Suggest improvements for this sheet",
    ],
  },
  {
    title: "Edit & Modify",
    icon: <Database className="h-4 w-4" />,
    color: "cyan",
    suggestions: [
      "Add a 7-day price change column",
      "Add SOL, AVAX, and DOT to this watchlist",
      "Remove rows with missing data",
    ],
  },
  {
    title: "Ask Questions",
    icon: <Table2 className="h-4 w-4" />,
    color: "cyan",
    suggestions: [
      "What does MC/TVL ratio mean?",
      "Which asset has the best metrics?",
      "Explain the columns in this sheet",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Mode-specific branding
// ─────────────────────────────────────────────────────────────────────────────

const modeBranding = {
  quick: {
    title: "Quick Ask",
    subtitle: "Fast answers for simple crypto questions",
    icon: <Zap className="h-7 w-7 text-amber-400" />,
    gradient: "from-amber-500/20 to-orange-500/20",
    border: "border-amber-500/20",
  },
  deep: {
    title: "Deep Research",
    subtitle: "Multi-agent analysis with comprehensive reports",
    icon: <Sparkles className="h-7 w-7 text-violet-400" />,
    gradient: "from-violet-500/20 to-fuchsia-500/20",
    border: "border-violet-500/20",
  },
  workspace: {
    title: "Workspace Builder",
    subtitle: "Create custom dashboards with natural language",
    icon: <Wrench className="h-7 w-7 text-emerald-400" />,
    gradient: "from-emerald-500/20 to-teal-500/20",
    border: "border-emerald-500/20",
  },
  sheets: {
    title: "Sheets Copilot",
    subtitle: "Create, analyze, and modify spreadsheets with AI",
    icon: <Table2 className="h-7 w-7 text-cyan-400" />,
    gradient: "from-cyan-500/20 to-blue-500/20",
    border: "border-cyan-500/20",
  },
};

// Map mode to categories
const modeCategories = {
  quick: quickCategories,
  deep: deepCategories,
  workspace: workspaceCategories,
  sheets: sheetsCategories,
};

// ─────────────────────────────────────────────────────────────────────────────
// Suggestion Card
// ─────────────────────────────────────────────────────────────────────────────

interface SuggestionCategory {
  title: string;
  icon: React.ReactNode;
  color: string;
  suggestions: string[];
}

const SuggestionCard: React.FC<{
  category: SuggestionCategory;
  onSelect: (suggestion: string) => void;
}> = ({ category, onSelect }) => (
  <div className="rounded-xl border border-gray-700/40 bg-gray-900/40 overflow-hidden">
    <div className={`flex items-center gap-2 px-3 py-2 bg-${category.color}-500/10 border-b border-${category.color}-500/20`}>
      <span className={`text-${category.color}-400`}>{category.icon}</span>
      <span className="text-sm font-medium text-white">{category.title}</span>
    </div>
    <div className="p-2">
      {category.suggestions.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(s)}
          className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800/50 hover:text-white transition-colors"
        >
          {s}
        </button>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ChatEmptyState: React.FC<ChatEmptyStateProps> = ({
  mode,
  onSuggestionClick,
}) => {
  const branding = modeBranding[mode];
  const categories = modeCategories[mode];

  // Get suggestion prompt text based on mode
  const getSuggestionPrompt = () => {
    switch (mode) {
      case "quick":
        return "Try a quick question";
      case "deep":
        return "Start a research project";
      case "workspace":
        return "Build something";
      case "sheets":
        return "Create a spreadsheet";
      default:
        return "Get started";
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="w-full max-w-3xl px-4">
        {/* Logo / Brand - changes based on mode */}
        <div className="flex flex-col items-center mb-6">
          <div className="mb-4">
            <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${branding.gradient} flex items-center justify-center border ${branding.border}`}>
              {branding.icon}
            </div>
          </div>

          <h1 className="text-2xl font-semibold text-white mb-1">
            {branding.title}
          </h1>
          <p className="text-gray-500 text-center text-sm max-w-md">
            {branding.subtitle}
          </p>
        </div>

        {/* Suggestion Categories - different for each mode */}
        <div>
          <p className="text-center text-xs text-gray-500 mb-4">
            {getSuggestionPrompt()}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {categories.map((cat) => (
              <SuggestionCard key={cat.title} category={cat} onSelect={onSuggestionClick} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatEmptyState;
